/**
 * Fabric order for dealers.
 * Pending = garments not yet given to a tailor, and not already shared.
 * Meters follow the size chart. Shirt meters are not set, so shirts stay out of the PDF.
 */

export const FASHION_FABRIC = "Fashion Fabric";
export const RAHUL_SYNTHETIC = "Rahul Synthetic";
export const DEALERS = [FASHION_FABRIC, RAHUL_SYNTHETIC];

const inches = (v) => {
    if (v === undefined || v === null || v === "") return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
};

export function parseFabricName(value) {
    const s = String(value || "").trim();
    if (!s) return { brand: "", line: "" };
    const m = s.match(/^(.+?)\s+-\s+(.+)$/);
    if (m) return { brand: m[1].trim(), line: m[2].trim() };
    return { brand: "", line: s };
}

/** Suiting: Raymond except Flexy, Vintage, and Matty → Fashion Fabric. Shirting: only Raymond Crystal. Rest → Rahul Synthetic. */
export function dealerForPiece(itemType, fabric) {
    const { brand, line } = parseFabricName(fabric);
    const whole = String(fabric || "").toLowerCase();
    const b = brand.toLowerCase();
    const l = line.toLowerCase();
    const raymond = b.includes("raymond") || whole.includes("raymond");
    const rahulRaymond = /fl[ae]xy|vintage|matty/.test(l) || /fl[ae]xy|vintage|matty/.test(whole);
    if (itemType === "shirt") {
        if (raymond && /cryst[ae]l/.test(whole)) return FASHION_FABRIC;
        return RAHUL_SYNTHETIC;
    }
    if (raymond && !rahulRaymond) return FASHION_FABRIC;
    return RAHUL_SYNTHETIC;
}

/** Coat meters from body chest (inches). 5XL / 6XL are not set. */
export function coatMeters(chest) {
    const n = inches(chest);
    if (n == null) return null;
    if (n <= 41) return 1.6;
    if (n <= 47) return 1.8;
    if (n <= 49) return 2.5;
    return null;
}

/** Pant meters from body waist (inches). Above size 46 is not set. */
export function pantMeters(waist) {
    const n = inches(waist);
    if (n == null) return null;
    if (n <= 45) return 1.2;
    if (n <= 47) return 1.4;
    return null;
}

/** Vest is 0.8, and 1.2 only when this body needs a 2.5 m coat. */
export function vestMeters(chest) {
    const coat = coatMeters(chest);
    if (coat == null && inches(chest) != null && inches(chest) > 49) return null;
    if (coat === 2.5) return 1.2;
    if (inches(chest) == null) return null;
    return 0.8;
}

export function pieceMeters(itemType, measurements) {
    const m = measurements || {};
    if (itemType === "coat") return coatMeters(m.chest);
    if (itemType === "pant") return pantMeters(m.waist);
    if (itemType === "vest_coat") return vestMeters(m.chest);
    return null;
}

const round1 = (n) => Math.round(n * 10) / 10;

/**
 * Pieces still to order for the current stall view.
 * `shared` is { garmentKey: timestamp } from CampusConfig/fabricOrder.
 */
export function buildFabricOrder({ garments, bookingsById, assignments, shared }) {
    const sent = shared || {};
    const dealers = Object.fromEntries(DEALERS.map((d) => [d, { rows: new Map(), unset: [], keys: [], meters: 0, pieces: 0 }]));
    let shirts = 0;

    (garments || []).forEach((g) => {
        if (!g || g.itemType === "tie") return;
        if (assignments?.[g.key]?.tailor) return;
        if (sent[g.key]) return;
        if (g.itemType === "shirt") { shirts += 1; return; }
        if (g.itemType !== "coat" && g.itemType !== "pant" && g.itemType !== "vest_coat") return;

        const booking = bookingsById?.[g.bookingId];
        const measurements = booking?.measurements || {};
        const meters = pieceMeters(g.itemType, measurements);
        const dealer = dealerForPiece(g.itemType, g.fabric);
        const bucket = dealers[dealer];
        bucket.keys.push(g.key);

        if (meters == null) {
            bucket.unset.push({
                key: g.key,
                invoiceNo: g.invoiceNo || "—",
                customerName: g.customerName || "—",
                itemName: g.itemName || g.itemType,
                fabric: g.fabric || "(No fabric)",
                shade: g.shade || "",
                chest: inches(measurements.chest),
                waist: inches(measurements.waist),
            });
            return;
        }

        const rk = `${String(g.fabric || "(No fabric)").trim().toLowerCase()}||${String(g.shade || "").trim().toLowerCase()}`;
        if (!bucket.rows.has(rk)) {
            bucket.rows.set(rk, {
                fabric: g.fabric || "(No fabric)",
                shade: g.shade || "",
                coat: 0, pant: 0, vest: 0, meters: 0,
            });
        }
        const row = bucket.rows.get(rk);
        if (g.itemType === "coat") row.coat += 1;
        else if (g.itemType === "pant") row.pant += 1;
        else row.vest += 1;
        row.meters = round1(row.meters + meters);
        bucket.meters = round1(bucket.meters + meters);
        bucket.pieces += 1;
    });

    const packs = DEALERS.map((name) => {
        const b = dealers[name];
        const rows = [...b.rows.values()].sort((a, c) => c.meters - a.meters || a.fabric.localeCompare(c.fabric));
        const unsetKeys = new Set(b.unset.map((u) => u.key));
        return {
            name,
            rows,
            unset: b.unset,
            keys: b.keys.filter((k) => !unsetKeys.has(k)),
            meters: b.meters,
            pieces: b.pieces,
        };
    });

    return {
        packs,
        shirts,
        shareKeys: packs.flatMap((p) => p.keys),
    };
}

